import { map, reduce, toPairs } from 'ramda'

import {
  AcquiringSettings,
  AcquiringItem,
  RefillType,
  RefillStyle,
} from 'app/dataTypes/acquiring/settings'

import type { Request } from '../../../index'

type ApiItem = {
  enabled: boolean //                       разрешено или нет
  commission: string | null //              размер комиссии
  commission_id: 0 | 1 //                   тип комиссии, где 0: +% к оплате, 1: -% к зачислению
  offer_url: string | null //               ссылка на оферту
  page_type: 'frame' | 'window' //          тип страницы, в МП не используется
  balance: string | null //                 текущий баланс
  min_sum: string | null //                 минимальная сумма платежа
  max_sum: string | null //                 максимальная сумма платежа
  school_id: number | null //               идентификатор школы
  accountable_id: number //                 идентификатор владельца счета зачисления
  accountable_type: 'Parent' | 'Student' // тип владельца счета зачисления
  recurrent_support: boolean //             поддержка рекуррентных платежей
}

type ApiRecordKey = 'primary' | 'cafeteria' | 'camp'
type RecordKey = keyof Omit<AcquiringSettings, 'availableRefillTypes'>

type ApiResult = Record<ApiRecordKey, Array<ApiItem>>

export const convertParams = (): [null, null] => [null, null]
export const convertResult = ({ primary, cafeteria, camp }: ApiResult): AcquiringSettings => {
  const pairs: Array<[RecordKey, Array<ApiItem>]> = toPairs({
    primary: primary || [],
    cafeteria: cafeteria || [],
    camp: camp || [],
  })

  const convertRecord = (
    rk: ApiRecordKey,
    items: Array<ApiItem>,
  ): Array<[string, AcquiringItem]> => {
    return map(
      ({
        accountable_id: id,
        accountable_type: ownerType,
        enabled,
        commission,
        commission_id: commissionID,
        offer_url: offerURL,
        page_type: pageType,
        balance,
        min_sum: minSum,
        max_sum: maxSum,
        school_id: schoolID,
        recurrent_support: recurrentSupport,
      }): [string, AcquiringItem] => {
        return [
          String(id),
          {
            id: String(id),
            isParentAndAccountOwner: rk === 'cafeteria' && ownerType === 'Parent',
            ownerType,
            enabled,
            commissionPercent:
              commission || typeof commission === 'number' ? parseFloat(commission) : null,
            commissionType: ((cId) => {
              if (cId === 0) return 'increase'
              if (cId === 1) return 'decrease'
              return null
            })(commissionID),
            offerURL,
            pageType,
            balance: balance || typeof balance === 'number' ? parseFloat(balance) : null,
            minSum: minSum || typeof minSum === 'number' ? parseFloat(minSum) : null,
            maxSum: maxSum || typeof maxSum === 'number' ? parseFloat(maxSum) : null,
            schoolID: schoolID ? String(schoolID) : null,
            refillStyle: recurrentSupport ? RefillStyle.Recurrent : RefillStyle.Common,
          },
        ]
      },
      items,
    )
  }

  const some = reduce(
    (acc, [kind, items]) => {
      acc[kind] = new Map(convertRecord(kind, items))
      return acc
    },
    {} as Omit<AcquiringSettings, 'availableRefillTypes'>,
    pairs,
  )

  const availableRefillTypes = [] as Array<RefillType>
  if (primary.find(({ enabled }) => enabled)) {
    availableRefillTypes.push(RefillType.Primary)
  }
  if (cafeteria.find(({ enabled }) => enabled)) {
    availableRefillTypes.push(RefillType.Cafeteria)
  }
  if (camp.find(({ enabled }) => enabled)) {
    availableRefillTypes.push(RefillType.Camp)
  }

  return {
    ...some,
    availableRefillTypes,
  }
}

export const GET_ACQUIRING_SETTINGS: Request<void, AcquiringSettings, void> = [
  'payments/acquiring2/settings',
  'get',
  ['data'],
]
